import { useLocation } from 'react-router-dom'
import { ModelChip } from '@/components/ModelChip'
import { cn } from '@/lib/cn'
import { useDensity } from '@/lib/density'
import { useTier } from '../motion/tiers'
import { workspaceStore } from '../workspace/store'
import { surfaceForPath } from './migration'
import { setV1Cookie } from './Interstitial'

/**
 * V2 appbar: mono crumb on the left; chrome controls on the right
 * (model chip, Work Mode, density, V1 escape, palette). No ThemeToggle
 * — V2 is dark-only.
 */
export function AppBarV2({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { pathname } = useLocation()
  const { tier, isWork, prmClamped, toggleTier } = useTier()
  const { density, toggle: toggleDensity } = useDensity()
  const surface = surfaceForPath(pathname)

  const workTitle = prmClamped
    ? 'Reduced motion is on (system) — motion stays at Work-level restraint'
    : isWork
      ? 'Work Mode on — engines idling. Click for full motion.'
      : 'Work Mode — instant motion, ambient off'

  return (
    <header className="v2-appbar">
      <div className="v2-crumb">
        <span className="v2-crumb-dot" aria-hidden="true" />
        <span>tovieos</span>
        <span aria-hidden="true">·</span>
        <span>mission control</span>
        <span aria-hidden="true">·</span>
        <span className="v2-crumb-surface">{surface?.label ?? 'Hub'}</span>
      </div>
      <div className="v2-appbar-actions">
        <ModelChip onClick={onOpenPalette} />
        <button
          type="button"
          className="v2-iconbtn v2-desktop-only"
          onClick={() => workspaceStore.popOut(pathname)}
          disabled={pathname === '/'}
          title={pathname === '/' ? 'Home stays on the floor' : 'Open this surface as a window'}
          aria-label="Open current surface as a window"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="4" y="9" width="11" height="11" rx="2" />
            <path d="M9 9V5a1 1 0 011-1h9a1 1 0 011 1v9a1 1 0 01-1 1h-4" />
          </svg>
        </button>
        <button
          type="button"
          className={cn('v2-iconbtn', isWork && 'active')}
          onClick={toggleTier}
          title={workTitle}
          aria-pressed={tier === 'work'}
          aria-label="Toggle Work Mode"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M12 21a9 9 0 119-9" />
            <path d="M12 12l5-3" strokeLinecap="round" />
            <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
          </svg>
        </button>
        <button
          type="button"
          className={cn('v2-iconbtn', density === 'compact' && 'active')}
          onClick={toggleDensity}
          title={`Density: ${density} — toggle (Ctrl+Shift+.)`}
          aria-pressed={density === 'compact'}
          aria-label="Toggle compact density"
        >
          {density === 'compact' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="3" width="8" height="8" rx="1" />
              <rect x="13" y="3" width="8" height="8" rx="1" />
              <rect x="3" y="13" width="8" height="8" rx="1" />
              <rect x="13" y="13" width="8" height="8" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="3" width="18" height="8" rx="1" />
              <rect x="3" y="13" width="18" height="8" rx="1" />
            </svg>
          )}
        </button>
        <button
          type="button"
          className="v2-btn"
          onClick={() => {
            setV1Cookie()
            window.location.reload()
          }}
          title="Switch back to Hub V1 (classic)"
        >
          V1
        </button>
        <button type="button" className="v2-btn" onClick={onOpenPalette} title="Command palette (⌘K / Ctrl+K)">
          <span className="v2-kbd">
            <span>⌘</span>
            <span>K</span>
          </span>
        </button>
      </div>
    </header>
  )
}
